/**
 * Nova AI engine — Zod schemas for API boundaries.
 *
 * Aliases: nova schemas, nova validation, nova zod
 */

import { z } from "zod";

import { getMaxLengthForKey, ICP_APPLY_MAPPINGS } from "./apply-mappings";
import { checkDistinctHookTags, HOOK_CHANNELS, HOOK_TAGS } from "./hooks";

export const novaAgentTypeSchema = z.enum([
  "diagnose",
  "plan",
  "assets",
  "grow",
]);

export const novaConversationStatusSchema = z.enum([
  "active",
  "paused",
  "completed",
  "abandoned",
]);

export const createNovaConversationSchema = z.object({
  agentType: novaAgentTypeSchema,
  title: z.string().optional(),
});

export const continueNovaConversationSchema = z.object({
  conversationId: z.string().uuid(),
});

export const sendNovaMessageSchema = z.object({
  conversationId: z.string().uuid(),
  content: z.string().min(1).max(10000),
});

export const getNovaConversationsSchema = z.object({
  agentType: novaAgentTypeSchema.optional(),
});

export const getNovaMessagesSchema = z.object({
  conversationId: z.string().uuid(),
});

export const renameNovaConversationSchema = z.object({
  conversationId: z.string().uuid(),
  title: z.string().trim().min(1).max(200),
});

export const applyDeliverableSchema = z.object({
  deliverableId: z.string().uuid(),
  fields: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .min(1),
});

export const loadProfileValuesSchema = z.object({
  deliverableId: z.string().uuid(),
});

export const adaptDeliverableSchema = z.object({
  deliverableId: z.string().uuid(),
});

/**
 * Queue-item review transitions exposed to the client. The server module's
 * broader status set includes `pending`, `generating`, `failed` which are
 * system-managed and not coach-assignable — we deliberately omit them here
 * so coaches can only approve, reject, or re-open a reviewable item.
 */
export const updateNovaQueueItemStatusSchema = z.object({
  queueItemId: z.string().uuid(),
  status: z.enum(["ready_for_review", "approved", "rejected"]),
  reviewNotes: z.string().max(2000).nullable().optional(),
});

// ---------------------------------------------------------------------------
// ICP Definition (28 data points)
// ---------------------------------------------------------------------------

export const icpDemographicsSchema = z.object({
  titleRoleLevel: z
    .string()
    .describe(
      "Title / role level of ideal client (e.g. 'Mid-level marketing managers')",
    ),
  industryVertical: z
    .string()
    .describe("Industry or vertical (e.g. 'B2B SaaS', 'Healthcare')"),
  ageRangeSweetSpot: z
    .string()
    .describe(
      "Age range and sweet spot with highest urgency (e.g. '35-50, sweet spot 40-45')",
    ),
  gender: z
    .string()
    .describe("Gender skew if relevant, or 'Mixed' with rationale"),
  geography: z
    .string()
    .describe(
      "Primary + secondary markets (e.g. 'US — NYC, SF, Austin; secondary: London, Toronto')",
    ),
  salaryRange: z
    .string()
    .describe("Compensation range for the role/industry/geography"),
  netWorthFinancialReality: z
    .string()
    .describe("Typical net worth and emotional relationship with money"),
});

/**
 * Canonical desirability-verdict band values. Single source of truth shared by
 * the deliverable schema (below), the pure `computeNicheVerdict` gate, and the
 * `score_niche` working-record schema — server files import these so a new band
 * added here flows everywhere instead of drifting per-file.
 */
export const NICHE_VERDICT_VALUES = [
  "strong",
  "workable",
  "weak",
  "kill",
] as const;

/** Canonical market-demand-signal values — same single-source-of-truth role. */
export const DEMAND_SIGNAL_VALUES = [
  "strong",
  "moderate",
  "weak",
  "absent",
] as const;

export const icpNicheValidationSchema = z.object({
  passionScore: z
    .string()
    .describe(
      "Coach's passion — overlap between their background and this niche, with evidence",
    ),
  painValidation: z
    .string()
    .describe(
      "Pain assessment: emotionally intense? time-sensitive? identity-threatening? expensive if ignored?",
    ),
  paymentAbilityWillingness: z
    .string()
    .describe(
      "Ability to pay (salary data) + willingness (pain urgency). Who writes the check?",
    ),
  densityWhereClusters: z
    .string()
    .describe(
      "Where this audience clusters: LinkedIn groups, associations, conferences, communities",
    ),
  fiftyInTenMinutesTest: z
    .string()
    .describe(
      "LinkedIn search query + estimated results — can you find 50 prospects in 10 minutes?",
    ),
  // ── Reach (PPP-DRU) — additive-optional so pre-reach ICPs still parse; the
  // save gate (`evaluateIcpReachGate`) is what requires it for a new niche lock.
  reachList: z
    .array(z.string().min(1))
    .optional()
    .describe(
      "Named real people in this niche the coach can reach directly or through one warm introduction — each entry a real name plus how they'd reach them (never a description of a type of person)",
    ),
  // ── Gate A desirability scoring (additive-optional; mirrors the Gate B pattern, commit b5a88df8) ──
  // Legacy free-text fields above remain authoritative for pre-Gate-A deliverables.
  painIntensity: z
    .number()
    .int()
    .min(1)
    .max(5)
    .optional()
    .describe("How acute the pain is, 1–5, research-grounded"),
  painEvidence: z
    .array(z.string())
    .optional()
    .describe("Citations / evidence supporting the pain assessment"),
  paymentAbility: z
    .number()
    .int()
    .min(1)
    .max(5)
    .optional()
    .describe("Ability to pay (income / budget reality), 1–5"),
  paymentWillingness: z
    .number()
    .int()
    .min(1)
    .max(5)
    .optional()
    .describe("Willingness to pay (urgency of the pain), 1–5"),
  density: z
    .number()
    .int()
    .min(1)
    .max(5)
    .optional()
    .describe("How findable / clustered this audience is, 1–5"),
  demandSignal: z
    .enum(DEMAND_SIGNAL_VALUES)
    .optional()
    .describe("Market demand signal from research"),
  verdict: z
    .enum(NICHE_VERDICT_VALUES)
    .optional()
    .describe("Code-computed desirability verdict"),
  verdictReasoning: z
    .string()
    .optional()
    .describe("Plain-language, evidence-citing reason for the verdict"),
  unverified: z
    .boolean()
    .optional()
    .describe("True when the verdict ran without live research evidence"),
  // Override receipt (set when a coach proceeds past a kill) — see plan R3
  overridden: z
    .boolean()
    .optional()
    .describe("True when a coach chose to proceed past a kill verdict"),
  overrideReason: z
    .string()
    .optional()
    .describe("Coach's stated reason for overriding a kill verdict"),
  verdictSnapshot: z
    .object({
      verdict: z.enum(NICHE_VERDICT_VALUES),
      painIntensity: z.number().int().min(1).max(5).optional(),
      paymentAbility: z.number().int().min(1).max(5).optional(),
      paymentWillingness: z.number().int().min(1).max(5).optional(),
      density: z.number().int().min(1).max(5).optional(),
      demandSignal: z.enum(DEMAND_SIGNAL_VALUES).optional(),
    })
    .optional()
    .describe(
      "Verdict + dimensions captured at override time, for the audit receipt",
    ),
});

export const icpPsychographicPortraitSchema = z.object({
  marriageRelationship: z
    .string()
    .describe(
      "Verified relationship context, 'Unknown', or an explicitly labeled hypothesis to validate",
    ),
  familyKids: z
    .string()
    .describe(
      "Verified family-stage context, 'Unknown', or an explicitly labeled hypothesis to validate",
    ),
  bodyHealth: z
    .string()
    .describe(
      "Verified buyer language about workload or wellbeing; never an inferred health condition or trajectory",
    ),
  socialBelonging: z
    .string()
    .describe(
      "Verified support or isolation context, or an explicitly labeled hypothesis to validate",
    ),
  identityStatement: z
    .string()
    .describe(
      "Capstone: '[Professional identity] at work. [Identity gap] in own life.'",
    ),
});

export const icpTriggerProblemSchema = z.object({
  coreDecision: z
    .string()
    .describe(
      "Binary decision distilled from the coach's problem description (e.g. 'Stay or start')",
    ),
  triggerMoment1TheMath: z
    .string()
    .describe("Trigger 1 — The Math: rational, undeniable realization"),
  triggerMoment2TheHijacking: z
    .string()
    .describe("Trigger 2 — The Hijacking: loss of agency moment"),
  triggerMoment3TheRealization: z
    .string()
    .describe("Trigger 3 — The Realization: urgency / awakening"),
  messagingHierarchy: z
    .string()
    .describe("Fixed: lead with rational trigger (The Math). Emotion follows."),
  entryPoint1: z
    .string()
    .describe("What event cracks the door open (from coach)"),
  entryPoint2: z
    .string()
    .describe("Industry-specific events creating the same emotional opening"),
  entryPoint3: z
    .string()
    .describe("AI / restructuring / displacement trends for this industry"),
});

export const icpObjectionsSchema = z.object({
  preCallObjections: z
    .array(z.string())
    .describe("3-4 objections based on ICP + price point + coaching category"),
  masterObjection: z
    .string()
    .describe("Strongest verified objection from the coach or buyer research"),
  whatTheyveTried: z
    .array(z.string())
    .describe("What the ICP has already tried + alternatives landscape"),
});

/**
 * Durable distinction between the market a coach is building toward and the
 * adjacent audience they can recruit now. Optional on the enclosing ICP so
 * deliverables saved before Canon v1 continue to parse unchanged.
 */
export const icpMarketPathSchema = z.object({
  destinationAudience: z
    .string()
    .min(1)
    .describe(
      "The single long-term, top-9% market the coach is building toward",
    ),
  startingRungAudience: z
    .string()
    .min(1)
    .describe(
      "The audience the coach can reach and recruit now; may equal the destination when direct reach is already strong",
    ),
  adjacencyRationale: z
    .string()
    .min(1)
    .describe(
      "Why the same urgent problem, buying logic, and first move of the coaching method carry from the starting rung to the destination",
    ),
});

export const icpDeliverableSchema = z.object({
  demographics: icpDemographicsSchema,
  nicheValidation: icpNicheValidationSchema,
  psychographicPortrait: icpPsychographicPortraitSchema,
  triggerProblemArchitecture: icpTriggerProblemSchema,
  objectionsLandscape: icpObjectionsSchema,
  marketPath: icpMarketPathSchema.optional(),
});

// ---------------------------------------------------------------------------
// Signature mechanism — the coach's method as named, sequenced IP
// ---------------------------------------------------------------------------

/**
 * The coach's method developed into distinctive, named, sequenced IP — the
 * "fingerprint" that turns a verbatim "here's what I do" into something
 * proprietary and repeatable. First-class so Build and the hook engine can
 * consume it directly. Honest nulls when the coach's method is still forming —
 * never a fabricated mechanism.
 */
export const offerSignatureMechanismSchema = z.object({
  name: z
    .string()
    .nullable()
    .describe(
      "The method's own name, distinct from the program/offer name. Null when the coach has no distinctive method yet — never fabricate one.",
    ),
  signatureMove: z
    .string()
    .describe(
      "The one move most uniquely the coach's — what peers in the niche don't do. When the method is still forming, an honest statement of that, not an invented move.",
    ),
  sequence: z
    .array(z.string())
    .describe(
      "The method as an ordered process with defensible logic (this step before that, and why). Empty when the method is still forming.",
    ),
  tiedToDifferentiator: z
    .boolean()
    .describe(
      "True when the signature move is also what sets the coach apart — method and differentiation are the same fact.",
    ),
});

// ---------------------------------------------------------------------------
// Core Offer (Plan step 5 — core-offer-design)
//
// The typed offer record from the Plan brief's §7 save list
// (docs/nova/briefs/2026-07-build/3-plan-agent-brief.md): promise, pain
// solved, signature mechanism, phases + deliverables, delivery model, price,
// optional guarantee, ownable angle, irresistibility notes — plus the
// SERVER-OWNED market-check verdict (+ override receipt) and the four
// value-lever scores. Its existence means "Plan is complete". Typed so the
// verdict and the future sales-page renderer can compute on these fields.
// ---------------------------------------------------------------------------

/** Canonical delivery formats — decided against the coach's ambition level. */
export const CORE_OFFER_DELIVERY_FORMATS = [
  "one_on_one",
  "group",
  "hybrid",
] as const;

/** Canonical guarantee types — encouraged, never mandatory. */
export const CORE_OFFER_GUARANTEE_TYPES = [
  "full_refund",
  "conditional",
  "do_more_work",
  "quiet_confidence",
] as const;

export const coreOfferPhaseSchema = z.object({
  name: z
    .string()
    .min(1)
    .describe("Phase name — a step of the signature method, in plain words"),
  description: z
    .string()
    .min(1)
    .describe(
      "What this phase does and why it sits here — the defensible logic (this before that, and why)",
    ),
  deliverables: z
    .array(z.string().min(1))
    .min(1)
    .describe(
      "The deliverables this phase produces — each converts a specific client problem from the enumerated list. The program is the solved list; low-value pieces are trimmed silently.",
    ),
});

export const coreOfferDeliveryModelSchema = z.object({
  format: z
    .enum(CORE_OFFER_DELIVERY_FORMATS)
    .describe(
      "1:1, group, or hybrid — decided against the coach's ambition (scale, hours, leverage appetite), never a default",
    ),
  rationale: z
    .string()
    .min(1)
    .describe("Why this format fits the coach's ambition and life shape"),
});

export const coreOfferPriceSchema = z.object({
  amountCents: z
    .number()
    .int()
    .positive()
    .describe(
      "Core offer price in CENTS (e.g. 400000 for $4,000). Typed on purpose so downstream surfaces can compute on it — never free text.",
    ),
  rationale: z
    .string()
    .min(1)
    .describe(
      "Why this number: anchored to what the result is worth to the client (never the coach's hours), informed by ambition, the market check's price corridor, and the Diagnose economics baseline when given",
    ),
});

export const coreOfferGuaranteeSchema = z.object({
  type: z
    .enum(CORE_OFFER_GUARANTEE_TYPES)
    .describe(
      "The guarantee type that fits the coach: full refund, conditional, do-more-work, or quiet confidence",
    ),
  wording: z
    .string()
    .min(1)
    .describe(
      "The guarantee in the coach's plain words ('If you do the work and don't get [result] in [time], here's what I'll do') — only what they can stand behind",
    ),
});

/**
 * The irresistibility pass, recorded. The three required behaviors of the
 * pass (skeptic read → named weakest lever → concrete fix, decision forced)
 * land here as notes alongside the #534 value-engineering fields the pass
 * reads and reworks. Optional at the top level for additive evolution; the
 * step-5 flow always runs the pass on new saves. The guarantee lives in the
 * deliverable's top-level `guarantee` field, not here.
 */
export const coreOfferIrresistibilitySchema = z.object({
  valueStack: z
    .array(z.string().min(1))
    .describe(
      "Everything the client gets and why each piece matters — the full value behind the price, in plain language. Never labelled a 'value stack' to the coach.",
    ),
  timeToFirstWin: z
    .string()
    .min(1)
    .describe(
      "The soonest real, tangible result — answers the quiet 'how long until anything changes?' worry",
    ),
  skepticRead: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The ICP meeting this offer cold, voiced in their own language — the real objections",
    ),
  weakestLever: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The one named problem from the pass — e.g. 'believability: a method with no visible proof'",
    ),
  chosenFix: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The concrete fix the coach chose — the pass ends with the offer changed, not assessed",
    ),
});

/**
 * SERVER-OWNED value-lever scores (dream outcome × perceived likelihood ÷
 * time delay × effort & sacrifice). Computed server-side during the
 * irresistibility pass and stripped from model payloads at the
 * create_deliverable choke point (see CORE_OFFER_SERVER_OWNED_FIELDS) — the
 * model must never write its own scores. The formula lives in the data and is
 * never named to the coach.
 */
export const coreOfferValueLeverScoresSchema = z.object({
  dreamOutcome: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Outcome vividness — how vivid and wanted the result is, 1–5"),
  perceivedLikelihood: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Believability — how much the ICP believes it will work, 1–5"),
  timeDelay: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Time-to-first-win — 5 means fast, 1 means distant"),
  effortSacrifice: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Effort required — 5 means light, 1 means heavy"),
});

/**
 * SERVER-OWNED market-check receipt recorded on the offer: the verdict the
 * offer was designed under, with any override receipt. Stamped from the
 * code-computed verdict at the create_deliverable choke point and stripped
 * from model payloads — a fabricated verdict can never persist.
 */
export const coreOfferMarketCheckSchema = z.object({
  verdict: z
    .enum(NICHE_VERDICT_VALUES)
    .describe("Code-computed desirability verdict the offer was built under"),
  verdictReasoning: z
    .string()
    .optional()
    .describe("Plain-language, evidence-citing reason for the verdict"),
  icpVersion: z
    .number()
    .int()
    .optional()
    .describe(
      "The icp_definition row version the verdict scored — a later ICP revision makes this verdict stale",
    ),
  overridden: z
    .boolean()
    .optional()
    .describe("True when the coach chose to proceed past a kill verdict"),
  overrideReason: z
    .string()
    .optional()
    .describe("Coach's stated reason for overriding a kill verdict"),
  marketPathChecks: z
    .object({
      destinationAudience: z.string().min(1),
      startingRungAudience: z.string().min(1),
      reachableContacts: z.number().int().min(10),
      adjacencyEvidence: z.array(z.string().min(1)).min(1),
    })
    .optional()
    .describe(
      "Receipts proving the destination was scored and the reachable starting rung passed the adjacency test",
    ),
});

export const coreOfferDeliverableSchema = z.object({
  name: z
    .string()
    .min(1)
    .describe(
      "The offer's name — quietly carries who it's for, the goal, the timeframe, a memorable word. Never explain a naming formula.",
    ),
  promise: z
    .string()
    .min(1)
    .describe(
      "The core transformation promised — so clear and valuable the right person doesn't hesitate",
    ),
  painSolved: z
    .string()
    .min(1)
    .describe("The pain this offer solves, in the ICP's own language"),
  signatureMechanism: offerSignatureMechanismSchema.describe(
    "The coach's own method, named and sequenced — honest nulls when it is still forming, never fabricated",
  ),
  phases: z
    .array(coreOfferPhaseSchema)
    .min(1)
    .describe(
      "The program as sequenced phases, each carrying the deliverables that solve the enumerated problems",
    ),
  deliveryModel: coreOfferDeliveryModelSchema,
  price: coreOfferPriceSchema,
  guarantee: coreOfferGuaranteeSchema
    .nullable()
    .describe(
      "Optional guarantee — encouraged, never mandatory. Null when the coach chooses not to offer one.",
    ),
  ownableAngle: z
    .string()
    .min(1)
    .describe(
      "The constructed differentiator from the market check — a specific, defensible position, not a biographical adjective",
    ),
  irresistibility: coreOfferIrresistibilitySchema.optional(),
  // Server-owned fields below — stripped from model payloads at the
  // create_deliverable choke point, populated by server code only.
  valueLeverScores: coreOfferValueLeverScoresSchema.optional(),
  marketCheck: coreOfferMarketCheckSchema.optional(),
});

// ---------------------------------------------------------------------------
// Voice Profile (voice-onboarding skill)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Profile Update (produced by the profile-update skill)
//
// Pre-adapted typed field updates — the skill is responsible for matching
// `ICP_APPLY_MAPPINGS` keys and respecting per-field max lengths, so the
// review UI can render directly without an extra LLM adapt step.
// ---------------------------------------------------------------------------

const PROFILE_UPDATE_KEYS = ICP_APPLY_MAPPINGS.map((m) => m.key) as [
  string,
  ...string[],
];

export const profileUpdateFieldSchema = z
  .object({
    key: z
      .enum(PROFILE_UPDATE_KEYS)
      .describe(
        "Profile field key from ICP_APPLY_MAPPINGS (e.g. 'profile.coachingTitle')",
      ),
    value: z
      .string()
      .describe(
        "Proposed new value, within the field's max length (enforced — over-limit values are rejected with the exact overage so you can shorten and retry)",
      ),
    rationale: z
      .string()
      .min(1)
      .describe(
        "One-line justification grounding the update in the coach's strategy + voice deliverables",
      ),
  })
  // Enforce per-field max length here so an over-limit value is rejected at
  // save time with an actionable message — the agent shortens and retries
  // instead of the overflow surfacing later as a downstream apply failure.
  .superRefine((field, ctx) => {
    const maxLength = getMaxLengthForKey(field.key);
    if (maxLength != null && field.value.length > maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `'${field.key}' is ${field.value.length} characters; limit is ${maxLength}. Shorten by at least ${field.value.length - maxLength} characters.`,
      });
    }
  });

export const profileUpdateDeliverableSchema = z.object({
  summary: z
    .string()
    .nullable()
    .describe("Optional one-line summary of the proposed update set"),
  fields: z
    .array(profileUpdateFieldSchema)
    .min(1)
    .describe("One entry per proposed profile field update"),
});

export const voiceProfileWritingSampleSchema = z.object({
  text: z
    .string()
    .min(1)
    .describe(
      "A piece of the coach's OWN writing, captured verbatim and unmodified — sentence breaks, rhythm, punctuation, and quirks all intact. This is the primary voice reference; downstream skills pattern-match their prose against it. Never paraphrase or clean it up.",
    ),
  source: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Where the sample came from, for context — e.g. 'LinkedIn post', 'client email', 'newsletter intro'. Optional.",
    ),
});

export const voiceProfileTraitsSchema = z.object({
  sentenceLength: z
    .string()
    .describe(
      "Sentence-length / rhythm habit drafted from the samples — e.g. 'short and punchy, rarely over 18 words' or 'long, layered, comma-spliced'.",
    ),
  punctuation: z
    .string()
    .describe(
      "Punctuation habits — e.g. 'no exclamation marks; frequent colons; never em-dashes; lots of one-line paragraphs'.",
    ),
  formality: z
    .string()
    .describe(
      "Register — e.g. 'casual and conversational, uses contractions' or 'polished and precise'.",
    ),
  emoji: z
    .string()
    .describe("Emoji habit — e.g. 'never', 'sparingly, only ✅', 'frequent'."),
  rhetoricalMoves: z
    .array(z.string())
    .default([])
    .describe(
      "Recurring structural moves observed in the samples — e.g. 'opens with a one-line question', 'ends on a short imperative', 'uses numbered lists'. May be empty.",
    ),
});

export const voiceProfileAspirationalReferenceSchema = z.object({
  reference: z
    .string()
    .min(1)
    .describe(
      "Content the coach admires (pasted text, a description, or a URL). Aspirational — NOT necessarily the coach's own voice, so kept separate from writingSamples.",
    ),
  whatWorks: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Why this piece represents their bar — structure, voice, a specific passage.",
    ),
});

/**
 * One channel's voice guide. The same coach does not sound the same in a public
 * post, an email to their list, and a one-to-one DM, so each channel carries its
 * own short calibration on top of the general profile.
 */
export const voiceChannelGuideSchema = z.object({
  register: z
    .string()
    .trim()
    .min(1)
    .describe(
      "How formal the coach is on this channel — e.g. 'casual, talks to one person' or 'polished, writes to a room'.",
    ),
  rhythm: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Sentence rhythm on this channel — e.g. 'short lines, frequent breaks' or 'longer flowing sentences'.",
    ),
  warmth: z
    .string()
    .trim()
    .min(1)
    .describe(
      "How warm the coach reads here — e.g. 'direct and dry' or \"warm, uses the reader's name\".",
    ),
  emoji: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Emoji policy for THIS channel specifically, which may differ from the general trait — e.g. 'never' or 'one at most, never in the first line'.",
    ),
  usePhrases: z
    .array(z.string())
    .default([])
    .describe(
      "Phrasings that belong on this channel. May be empty when the channel has no phrasings of its own.",
    ),
  avoidPhrases: z
    .array(z.string())
    .default([])
    .describe(
      "Phrasings that do not belong on this channel even though they are fine elsewhere. May be empty.",
    ),
  ctaStyle: z
    .string()
    .trim()
    .min(1)
    .describe(
      "How the coach asks on this channel — e.g. 'direct ask, one line' or 'soft invitation, no question mark'.",
    ),
  example: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Two or three sentences written in this channel's voice, confirmed or corrected by the coach. The calibration exemplar downstream skills pattern-match against.",
    ),
});

/**
 * All three channel guides. The skill saves the complete set or none of it —
 * a partial set would leave asset skills silently falling back to the general
 * profile for one channel while honoring a guide for another.
 */
export const voiceProfileChannelsSchema = z.object({
  social: voiceChannelGuideSchema.describe(
    "Voice guide for public posts and articles.",
  ),
  email: voiceChannelGuideSchema.describe(
    "Voice guide for newsletters and email sequences.",
  ),
  dm: voiceChannelGuideSchema.describe(
    "Voice guide for direct messages and outreach openers.",
  ),
});

export const voiceProfileDeliverableSchema = z.object({
  // Primary signal: the coach's own writing, stored verbatim and injected as
  // few-shot exemplars. Research is consistent that showing real samples beats
  // any verbal description for voice fidelity (e.g. Claude's Styles feature).
  writingSamples: z
    .array(voiceProfileWritingSampleSchema)
    .min(1)
    .describe(
      "1–3 verbatim samples of the coach's OWN writing — the spine of the voice. If the coach has nothing to paste, the skill writes tone options, the coach picks/blends, and the approved paragraph is saved here as a sample.",
    ),
  signaturePhrases: z
    .array(z.string())
    .default([])
    .describe(
      "Words and phrases the coach actually uses and wants kept — a literal positive list (the inverse of neverSay). e.g. 'real talk', \"here's the thing\", 'let's be honest'.",
    ),
  neverSay: z
    .array(z.string())
    .default([])
    .describe(
      "Coach's never-list: literal words, phrases, or topics they do not want Nova (or any sub-agent) to ever use on their behalf (e.g. 'unlock', 'game-changer', 'limited spots').",
    ),
  styleRules: z
    .array(z.string())
    .default([])
    .describe(
      "Explicit do/follow rules the coach stated that the samples and lists don't already capture — e.g. 'avoid almost-absolute words like \"most\"', 'don't make definitive blanket claims'. Concrete and generalizable, not the example phrase that prompted it. May be empty.",
    ),
  traits: voiceProfileTraitsSchema.describe(
    "Concrete, observable voice traits — drafted from the writingSamples and confirmed by the coach. These ground the abstraction in what's actually in the samples rather than a vague label.",
  ),
  aspirationalReference: voiceProfileAspirationalReferenceSchema
    .nullable()
    .optional()
    .describe(
      "Optional piece of content the coach admires, kept separate from their own voice (writingSamples).",
    ),
  // Optional and nullable so every profile calibrated before channel guides
  // existed still parses — `edit_deliverable` validates the stored row against
  // this schema before editing, so a required field here would make those rows
  // uneditable.
  channels: voiceProfileChannelsSchema
    .nullable()
    .optional()
    .describe(
      "Per-channel voice guides (social, email, dm). All three or none — never a partial set. Absent on profiles calibrated before channel guides existed.",
    ),

  // --- Legacy fields (pre-redesign profiles) -------------------------------
  // Kept optional so older voice_profile rows still satisfy the type and the
  // injected directive can fall back to them when a coach hasn't re-calibrated.
  // The current voice-onboarding skill no longer produces these.
  tonePreference: z.string().optional(),
  toneSample: z.string().nullable().optional(),
  toneNotes: z.string().nullable().optional(),
  qualityAnchor: z.string().optional(),
  qualityAnchorNotes: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Agent 2 skills landing in M1–M5 — placeholder shapes only.
//
// Each skill PR refines its own schema with stricter validation (length
// limits, enum values, exact tuple counts). For M0 the schemas just need
// to round-trip data through `create_deliverable` and the JSONB column
// without rejecting anything well-formed.
//
// Aliases: linkedin post schema, outreach scripts schema, mini sprint schema,
//          video scripts schema, agent 2 placeholders
// ---------------------------------------------------------------------------

/**
 * LinkedIn post body guardrails — encode the non-negotiable structural
 * rules. The skill's instructions handle craft-level rules (em-dashes,
 * banned phrases, 800–1300 char target, hook quality) — those live as
 * prose so they can evolve without a schema change. This schema only
 * rejects content that is structurally unfit for LinkedIn (too long,
 * hashtags inline, emoji, outbound links).
 */

/** Reject body content that contains an emoji codepoint. */
const EMOJI_PATTERN = /\p{Extended_Pictographic}/u;
/** Reject hashtags inside the body — final-line hashtags handled in instructions. */
const HASHTAG_PATTERN = /(^|\s)#\w/;
/**
 * Matches an outbound/external link (absolute `http(s)://` URL or a bare
 * `www.` host). Relative DCM links (`/my-coaching/...`) do not match, so this
 * doubles as an "external link" detector. Reused by the CIH article tool to
 * enforce its zero-external-links brand-safety rule.
 */
export const URL_PATTERN = /https?:\/\/|www\./i;

/**
 * LinkedIn's own post-length ceiling. Named (rather than inline) so the
 * coach-edit field config mirrors the exact cap this schema enforces.
 */
export const LINKEDIN_POST_BODY_MAX_CHARS = 3000;

/** Ceiling for the optional first-comment drop that accompanies a post. */
export const LINKEDIN_POST_FIRST_COMMENT_MAX_CHARS = 1500;

const linkedInPostBody = z
  .string()
  .min(1)
  .max(
    LINKEDIN_POST_BODY_MAX_CHARS,
    `Body exceeds ${LINKEDIN_POST_BODY_MAX_CHARS} characters — LinkedIn post is too long`,
  )
  .refine((s) => !HASHTAG_PATTERN.test(s), {
    message:
      "Body contains hashtags. LinkedIn post bodies must be hashtag-free.",
  })
  .refine((s) => !EMOJI_PATTERN.test(s), {
    message: "Body contains emoji. LinkedIn post bodies must be emoji-free.",
  })
  .refine((s) => !URL_PATTERN.test(s), {
    message:
      "Body contains an outbound link. Move the link to the first comment instead.",
  });

/**
 * Strategic content format — one of the 6 archetypes the linkedin-post
 * skill produces. The skill picks the format first, then the structure,
 * hook craft, and CTA selection all flow from that choice.
 */
const linkedInPostContentFormatSchema = z.enum([
  "Hero Post",
  "Client Transformation",
  "Value Post",
  "Two-Step",
  "Contrarian",
  "List Post",
]);

const linkedInPostAwarenessLevelSchema = z.enum([
  "unaware",
  "problem",
  "solution",
]);

/**
 * Angle used by the DCM amplification companion post. The amplification
 * targets a warmer DCM-platform audience and must come at the same topic
 * from a different angle than the personal LinkedIn post.
 */
const linkedInPostAmplificationAngleSchema = z.enum([
  "counter-objection",
  "secondary-insight",
  "behind-the-scenes",
]);

export const linkedInPostAmplificationSchema = z.object({
  body: linkedInPostBody,
  contentFormat: linkedInPostContentFormatSchema,
  angle: linkedInPostAmplificationAngleSchema,
});

/**
 * A post only earns a reaction from the right reader when it is built out
 * of that reader's own particulars. `icpMarkers` and `customerPhrase` turn
 * that from a hope into a checkable claim: the model has to name the
 * grounding it used, and every named string has to survive a literal
 * lookup in the body it is saving.
 *
 * The base shape, without that cross-field grounding check — kept separate
 * because the check is a GENERATION-TIME gate on model output, not a
 * property of a well-formed post. `linkedInPostDeliverableSchema` below adds
 * it back for everything that validates a model-authored save. The
 * coach-edit merge gate (`DELIVERABLE_EDIT_CONFIGS.linkedin_post.mergeSchema`
 * in deliverable-edit-config.ts) validates against this base shape instead:
 * a coach rewriting the prose is not restating Nova's grounding claim, so
 * binding their hand-edit to phrases they didn't write and can't see would
 * be a dead end, not a quality gate.
 */
export const linkedInPostContentSchema = z.object({
  sourceArticleId: z
    .string()
    .uuid()
    .nullable()
    .describe(
      "id of the coaching_articles row this post repurposes (from list_published_articles). Null for standalone posts (the default).",
    ),
  contentFormat: linkedInPostContentFormatSchema.describe(
    "Strategic content format (CF1–CF6): Hero Post, Client Transformation, Value Post, Two-Step, Contrarian, or List Post.",
  ),
  awarenessLevel: linkedInPostAwarenessLevelSchema.describe(
    "Awareness level the post targets — chosen against the coach's ICP awareness mix.",
  ),
  body: linkedInPostBody.describe(
    "Full post body, plain text. Target 800–1300 chars. Single-line paragraphs, white space between every 1–2 sentences. No hashtags, no emoji, no outbound links.",
  ),
  firstComment: z
    .string()
    .min(1)
    .max(LINKEDIN_POST_FIRST_COMMENT_MAX_CHARS)
    .nullable()
    .describe(
      "Optional first-comment text dropped within 5 minutes of posting. Null when the post drives comments rather than click-through.",
    ),
  amplification: linkedInPostAmplificationSchema
    .nullable()
    .describe(
      "Optional DCM amplification companion post — shorter, different angle, published by the coach after the LinkedIn post.",
    ),
  postingWindow: z
    .string()
    .min(1)
    .describe(
      "Recommended posting window — typically Tuesday–Thursday 7–9am ICP timezone.",
    ),
  rationale: z
    .string()
    .min(1)
    .describe(
      "Why this content format + awareness level combination fits the spine and the coach's ICP, plus the hook angle chosen.",
    ),
  icpMarkers: z
    .array(z.string().min(1))
    .min(2, "A post needs at least two concrete ICP markers in the body")
    .describe(
      "At least two concrete ICP markers the body uses: role, situation, constraint, number, or context detail only the target reader would recognize. Each entry must appear in `body` word for word.",
    ),
  customerPhrase: z
    .string()
    .min(1)
    .describe(
      "One phrase taken word for word from how the coach's clients or ICP actually talk (ICP pain language, voice profile, or hook library). Never invented. Must appear in `body` word for word.",
    ),
});

/**
 * The full generation-time gate: `linkedInPostContentSchema` plus the
 * grounding cross-check enforced when the model saves via `create_deliverable`.
 * See the TSDoc above `linkedInPostContentSchema` for why the coach-edit merge
 * gate deliberately validates against the base shape instead of this one.
 */
export const linkedInPostDeliverableSchema =
  linkedInPostContentSchema.superRefine((post, ctx) => {
    const body = post.body.toLowerCase();

    post.icpMarkers.forEach((marker, index) => {
      if (!body.includes(marker.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["icpMarkers", index],
          message: `ICP marker "${marker}" does not appear in the post body. List the markers the body actually uses, or write this one into the post.`,
        });
      }
    });

    if (!body.includes(post.customerPhrase.toLowerCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customerPhrase"],
        message: `Customer phrase "${post.customerPhrase}" does not appear in the post body. Quote it in the post, or record the phrase the body actually uses.`,
      });
    }
  });

/**
 * Outreach scripts deliverable schema (v4 with v2 saved-row compatibility).
 *
 * Enforces the current design while preserving existing v2 rows:
 *  - `connectionRequest` is a discriminated union of {mode:"blank"} or
 *    {mode:"noted", body, tokens}.
 *  - `scripts[]` MUST contain exactly one entry for each of the 4 canonical
 *    types: `opening_dm`, `warm_reconnect`, `reactive_reply`,
 *    `comment_fulfillment` — no duplicates, no missing types. Each type has
 *    its own shape (legacy opening_dm has body + engagementSignalBody; v4
 *    opening_dm stores the staged pain-check, reply branches, and link message;
 *    legacy warm/reactive rows carry `openers[]` + `softPitchBody`, while v4
 *    rows store the pain check and reply branches separately;
 *    comment_fulfillment is a single body).
 *  - `followUps.afterColdSilent` is exactly 1 touch with intent
 *    "article-share". `followUps.afterInterestSilent` is exactly 2 touches:
 *    "direct-check-in" then "name-nudge", in that order.
 *  - `targeting.linkedinSearch` is the ICP-derived LinkedIn search string;
 *    `targeting.volume` is the coach-specific sustainable cadence (the field
 *    name remains for compatibility), derived from `cadenceBasis` for v4 rows.
 */
export const outreachScriptTypeSchema = z.enum([
  "opening_dm",
  "warm_reconnect",
  "reactive_reply",
  "comment_fulfillment",
]);

export const outreachFollowUpIntentSchema = z.enum([
  "article-share",
  "direct-check-in",
  "name-nudge",
]);

const legacyOpeningDmDraftSchema = z.object({
  type: z.literal("opening_dm"),
  flow: z.never().optional(),
  body: z.string().min(1),
  engagementSignalBody: z.string().min(1),
  tokens: z.array(z.string()),
  when: z.string().min(1),
});

const twoStepOpeningDmDraftSchema = z.object({
  type: z.literal("opening_dm"),
  flow: z.literal("two_step"),
  painCheckBody: z.string().min(1),
  engagementSignalPainCheckBody: z.string().min(1),
  positiveReplyBody: z.string().min(1),
  negativeReplyBody: z.string().min(1),
  detailsLinkBody: z.string().min(1),
  tokens: z.array(z.string()),
  when: z.string().min(1),
});

const multiStepBaseSchema = z.object({
  type: z.enum(["warm_reconnect", "reactive_reply"]),
  openers: z
    .array(
      z.object({
        label: z.string().min(1),
        body: z.string().min(1),
      }),
    )
    .min(1, { message: "openers must contain at least 1 entry" })
    .max(4, { message: "openers must contain at most 4 entries" }),
  tokens: z.array(z.string()),
  when: z.string().min(1),
});

const legacyMultiStepScriptDraftSchema = multiStepBaseSchema.extend({
  flow: z.never().optional(),
  softPitchBody: z.string().min(1),
});

const conditionalMultiStepScriptDraftSchema = multiStepBaseSchema.extend({
  flow: z.literal("conditional"),
  painCheckBody: z.string().min(1),
  positiveReplyBody: z.string().min(1),
  negativeReplyBody: z.string().min(1),
  detailsLinkBody: z.string().min(1),
});

const commentFulfillmentDraftSchema = z.object({
  type: z.literal("comment_fulfillment"),
  body: z.string().min(1),
  tokens: z.array(z.string()),
  when: z.string().min(1),
});

export const outreachScriptDraftSchema = z.union([
  legacyOpeningDmDraftSchema,
  twoStepOpeningDmDraftSchema,
  legacyMultiStepScriptDraftSchema,
  conditionalMultiStepScriptDraftSchema,
  commentFulfillmentDraftSchema,
]);

export const outreachConnectionRequestSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("blank") }),
  z.object({
    mode: z.literal("noted"),
    body: z.string().min(1),
    tokens: z.array(z.string()),
  }),
]);

export const outreachFollowUpTouchSchema = z.object({
  delayDays: z.number().int().nonnegative(),
  body: z.string().min(1),
  intent: outreachFollowUpIntentSchema,
});

export const outreachScriptsDeliverableSchema = z
  .object({
    connectionRequest: outreachConnectionRequestSchema,
    scripts: z.array(outreachScriptDraftSchema).length(4, {
      message: "scripts must contain exactly 4 entries (one per script type)",
    }),
    followUps: z.object({
      afterColdSilent: z.array(outreachFollowUpTouchSchema).length(1, {
        message: "afterColdSilent must contain exactly 1 touch",
      }),
      afterInterestSilent: z.array(outreachFollowUpTouchSchema).length(2, {
        message: "afterInterestSilent must contain exactly 2 touches",
      }),
    }),
    targeting: z.object({
      linkedinSearch: z.string().min(1),
      volume: z
        .array(
          z.object({
            channel: z.string().min(1),
            recommended: z.string().min(1),
          }),
        )
        .min(1),
    }),
    cadenceBasis: z
      .object({
        availableHoursPerWeek: z.number().positive().max(80),
        currentActivity: z.string().min(1),
        ambition: z.string().min(1),
        rationale: z.string().min(1),
      })
      .optional(),
  })
  .superRefine((val, ctx) => {
    const expectedTypes = outreachScriptTypeSchema.options;
    const seenTypes = new Set(val.scripts.map((s) => s.type));
    for (const t of expectedTypes) {
      if (!seenTypes.has(t)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["scripts"],
          message: `scripts is missing required type "${t}"`,
        });
      }
    }
    if (seenTypes.size !== val.scripts.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scripts"],
        message: "scripts must not contain duplicate types",
      });
    }

    const usesV4Flow = val.scripts.some(
      (script) => "flow" in script && script.flow !== undefined,
    );
    const coldTouch = val.followUps.afterColdSilent[0];
    if (usesV4Flow) {
      const expectedFlows = {
        opening_dm: "two_step",
        warm_reconnect: "conditional",
        reactive_reply: "conditional",
      };
      for (const [type, expectedFlow] of Object.entries(expectedFlows)) {
        const script = val.scripts.find((candidate) => candidate.type === type);
        if (!script || !("flow" in script) || script.flow !== expectedFlow) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["scripts"],
            message: `v4 outreach must use flow "${expectedFlow}" for ${type}`,
          });
        }
      }
    }

    if (usesV4Flow && !val.cadenceBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cadenceBasis"],
        message:
          "cadenceBasis is required for v4 outreach so cadence is derived from available hours, current activity, and ambition",
      });
    }

    if (usesV4Flow) {
      const [directCheckIn, nameNudge] = val.followUps.afterInterestSilent;
      if (
        directCheckIn &&
        (directCheckIn.delayDays < 2 || directCheckIn.delayDays > 3)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["followUps", "afterInterestSilent", 0, "delayDays"],
          message: "v4 direct check-in must be sent after 2 to 3 days",
        });
      }
      if (nameNudge && (nameNudge.delayDays < 4 || nameNudge.delayDays > 5)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["followUps", "afterInterestSilent", 1, "delayDays"],
          message: "v4 name nudge must follow after another 4 to 5 days",
        });
      }
      if (coldTouch && (coldTouch.delayDays < 5 || coldTouch.delayDays > 7)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["followUps", "afterColdSilent", 0, "delayDays"],
          message: "v4 cold article share must be sent after 5 to 7 days",
        });
      }
    }

    if (coldTouch && coldTouch.intent !== "article-share") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["followUps", "afterColdSilent", 0, "intent"],
        message:
          'afterColdSilent[0].intent must be "article-share" (the only valid intent for cold-silent follow-up)',
      });
    }

    const interestIntents = val.followUps.afterInterestSilent.map(
      (t) => t.intent,
    );
    if (
      interestIntents.length === 2 &&
      (interestIntents[0] !== "direct-check-in" ||
        interestIntents[1] !== "name-nudge")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["followUps", "afterInterestSilent"],
        message:
          'afterInterestSilent must use intents ["direct-check-in", "name-nudge"] in that order',
      });
    }
  });

/**
 * Mini Sprint deliverable — 5-day entry-offer architecture from spec §3
 * Sub-Agent 2. The schema enforces structural invariants that the
 * `mini-sprint` skill must respect:
 *
 *   - exactly 5 sessions, one per day (Day 1–5, in order)
 *   - exactly 3 beliefs (the entry-offer's "three beliefs at completion")
 *   - exactly 10 hook variations (different angles, not rewordings)
 *   - exactly 3 landing-page bullets (3 hook lines)
 *   - 3–5 deliverable categories (the audit's measurable axes)
 *
 * Quality concerns (e.g. domino-homework chain, "valuable-standalone-but-
 * incomplete") live in the skill's instructions.md — the schema only
 * rejects content that is structurally unfit for the deliverable shape.
 */

const MINI_SPRINT_SESSION_COUNT = 5;
const MINI_SPRINT_BELIEF_COUNT = 3;
const MINI_SPRINT_HOOK_COUNT = 10;
const MINI_SPRINT_CATEGORIES_MIN = 3;
const MINI_SPRINT_CATEGORIES_MAX = 5;

/**
 * One hook variation, carrying the hook library's mechanism `tag` and its
 * `channelFit` alongside the line. Both are REQUIRED here: the taxonomy is
 * what makes a saved hook findable — a row with a null tag or null channel
 * fit is invisible to every filtered `list_hooks` call — and neither label
 * can be recovered from the line afterwards. A model that omits one gets a
 * validation error and rewrites the variation, which is cheaper than a
 * silently unlabelled hook bank.
 */
export const miniSprintHookVariationSchema = z.object({
  text: z.string().min(1).describe("The hook line itself."),
  tag: z
    .enum(HOOK_TAGS)
    .describe(
      "Which mechanism this line pulls with — one per hook, the one carrying most of the pull. Required.",
    ),
  channelFit: z
    .array(z.enum(HOOK_CHANNELS))
    .min(1)
    .describe(
      'The channels this line reads well in — any of "social", "email", "dm". Judged per line, not all three by default. Required, and never empty.',
    ),
});

export const miniSprintSessionSchema = z.object({
  day: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Day index, 1–5 (sessions array must be ordered Day 1 → Day 5)"),
  theme: z
    .string()
    .min(1)
    .describe("One-line theme for the session — e.g. 'Stakes' or 'Clarity'"),
  objective: z
    .string()
    .min(1)
    .describe(
      "What this session moves the client toward — must support at least one of the three beliefs",
    ),
  homeworkOut: z
    .string()
    .nullable()
    .describe(
      "Homework the client leaves with — feeds the next session's input (sessions 1–3 in the domino chain). Null on Day 5.",
    ),
  homeworkIn: z
    .string()
    .nullable()
    .describe(
      "Homework input expected at the start of this session — output of the previous session's homeworkOut. Null on Day 1.",
    ),
  durationMinutes: z
    .number()
    .int()
    .positive()
    .describe("Session length in minutes — typically 30–45"),
});

/** One objection and the reframe the coach answers it with. */
const objectionBranchSchema = z.object({
  objection: z.string().min(1),
  reframe: z.string().min(1),
});

/**
 * The sales conversation on the Mini Sprint's Day 5, written for the coach's
 * own use. Optional on the schema so Mini Sprints saved before it existed
 * still parse; the skill's instructions require it for every new save.
 */
const miniSprintSalesConversationSchema = z
  .object({
    opening: z
      .string()
      .min(1)
      .describe("How the coach opens the Day-5 conversation"),
    qualifying: z
      .string()
      .min(1)
      .describe(
        "The questions that confirm fit, building on what the client did and saw during the sprint",
      ),
    objectionBranches: z
      .array(objectionBranchSchema)
      .min(
        1,
        "closeMechanics.salesConversation.objectionBranches must contain at least 1 branch",
      )
      .describe(
        "The main objections from icp_definition.objectionsLandscape, each with the coach's response",
      ),
    pricingOpener: z
      .string()
      .min(1)
      .describe(
        "How the coach brings up price — derived from core_offer.price: what the result is worth to the client, then the number",
      ),
    closeOptions: z
      .array(z.string().min(1))
      .min(
        1,
        "closeMechanics.salesConversation.closeOptions must contain at least 1 option",
      )
      .describe(
        "Ways to close — typically the core offer, a written proposal, or an honest not-yet",
      ),
  })
  .describe(
    "Sales-conversation guide for the Day-5 close — branches, not a teleprompter",
  );

const miniSprintNotNowFollowUpSchema = z
  .object({
    message: z
      .string()
      .min(1)
      .describe("What the coach sends a client who says not now"),
    timing: z
      .string()
      .min(1)
      .describe(
        "When the coach sends it, e.g. 'the next morning, then once more at three weeks'",
      ),
  })
  .describe("Follow-up for a client who says 'not now' on Day 5");

export const miniSprintDeliverableSchema = z
  .object({
    sprintName: z
      .string()
      .min(1)
      .describe("Sprint name, agreed with the coach"),
    sessions: z
      .array(miniSprintSessionSchema)
      .length(
        MINI_SPRINT_SESSION_COUNT,
        `sessions must contain exactly ${MINI_SPRINT_SESSION_COUNT} entries (Day 1–5)`,
      )
      .describe("5-day session structure, ordered Day 1 → Day 5"),
    threeBeliefs: z
      .array(z.string().min(1))
      .length(
        MINI_SPRINT_BELIEF_COUNT,
        `threeBeliefs must contain exactly ${MINI_SPRINT_BELIEF_COUNT} beliefs`,
      )
      .describe(
        "The 3 beliefs the client must hold by Day 5 to want the core offer, agreed with the coach",
      ),
    deliverable: z
      .object({
        name: z
          .string()
          .min(1)
          .describe("Named artifact — e.g. 'The Crossroads Audit'"),
        format: z
          .string()
          .min(1)
          .describe("Format — e.g. 'one-page scorecard', 'PDF audit'"),
        categories: z
          .array(z.string().min(1))
          .min(
            MINI_SPRINT_CATEGORIES_MIN,
            `deliverable.categories must contain ${MINI_SPRINT_CATEGORIES_MIN}–${MINI_SPRINT_CATEGORIES_MAX} categories`,
          )
          .max(
            MINI_SPRINT_CATEGORIES_MAX,
            `deliverable.categories must contain ${MINI_SPRINT_CATEGORIES_MIN}–${MINI_SPRINT_CATEGORIES_MAX} categories`,
          )
          .describe("3–5 measurable axes the deliverable scores"),
        description: z
          .string()
          .min(1)
          .describe(
            "What the artifact contains and why it is valuable standalone but incomplete without the core offer",
          ),
      })
      .describe("Named artifact the client leaves the sprint with"),
    closeMechanics: z
      .object({
        creditNote: z
          .string()
          .min(1)
          .describe("Sprint price → core offer credit note (no hard sell)"),
        transitionScript: z
          .string()
          .min(1)
          .describe(
            "Day-5 transition conversation script (coaching, not pitch)",
          ),
        salesConversation: miniSprintSalesConversationSchema.optional(),
        notNowFollowUp: miniSprintNotNowFollowUpSchema.optional(),
      })
      .describe("Day-5 close mechanics — sprint creates inevitability"),
    sprintMode: z
      .enum(["free", "paid"])
      .optional()
      .describe(
        "free (default) or paid. NOVA recommends free first to build proof, then paid once it converts.",
      ),
    sprintPrice: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .optional()
      .describe(
        "Sprint price in CENTS when paid (e.g. 4700 for $47). null/omitted when free or the coach hasn't set a number yet.",
      ),
    // Objects only. This schema's one consumer is `create_deliverable`, the
    // model's write path, and a bare string would save as a hook with a null
    // tag and channel fit. Sprints stored before the taxonomy still hold bare
    // strings; the readers (`MiniSprintView`, `mapMiniSprintDeliverableToHookRows`)
    // accept both and never re-parse stored data with this schema.
    hookVariations: z
      .array(miniSprintHookVariationSchema)
      .length(
        MINI_SPRINT_HOOK_COUNT,
        `hookVariations must contain exactly ${MINI_SPRINT_HOOK_COUNT} angles — different angles, not rewordings`,
      )
      .superRefine((variations, ctx) => {
        const diversity = checkDistinctHookTags(
          variations.map((variation) => variation.tag),
        );
        if (!diversity.ok) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: diversity.message,
          });
        }
      })
      .describe(
        "Exactly 10 different angles for sprint promotion — one per entry point, one per trigger moment, one per psychographic dimension. Not rewordings of the same hook. Each entry is { text, tag, channelFit }: the line, the mechanism it pulls with, and the channels it reads well in.",
      ),
    problem: z
      .object({
        headline: z
          .string()
          .min(1)
          .describe(
            'Problem-section headline for the public page — name WHY this audience has the problem the coach solves ("Why most [audience] [bad outcome]"), paraphrased from the ICP\'s triggerProblemArchitecture. No trailing period.',
          ),
        description: z
          .string()
          .min(1)
          .describe(
            "A few short paragraphs (rendered as markdown) that validate the pain then reframe it as structural: pain-led opener, concrete detail, a generalized coping line (no specific tactic), an outside-fine contrast, and a truth-line. Optional soft future-cost close. In the coach's voice, sensitive-subject framing, no clinical language.",
          ),
      })
      .describe(
        "Problem framing rendered as page copy — feeds the Mini Sprint hero's struggles section",
      ),
    ctaUrl: z
      .string()
      .nullable()
      .refine(
        // Match the offer-side ctaUrl refinement (src/lib/schemas/offer.ts):
        // reject unsafe URL schemes but deliberately do NOT enforce full URL
        // format — coaches may supply bare hosts like `calendly.com/jane`
        // that the offer page normalizes via `toExternalHref` at render time.
        (val) => !val || !/^(javascript|data|vbscript):/iu.test(val.trim()),
        { message: "Unsafe URL protocol" },
      )
      .describe(
        "Coach's booking page URL (Calendly, Cal.com, contact page, etc.). Optional — null if the coach doesn't have one yet; they can fill it in the form after approval. Bare hosts like `calendly.com/jane` are allowed and normalized to https:// at render time.",
      ),
    landingPage: z
      .object({
        headline: z
          .string()
          .min(1)
          .describe(
            "Hero H1 — the specific outcome the SPRINT delivers (clarity / first step / a plan), in a timeframe, for the audience. Cannibalization-safe, not the core offer's end-state. No trailing period.",
          ),
        subhead: z
          .string()
          .min(1)
          .describe(
            'Hero H2 — the load-bearing "without" line: three things the prospect has tried, framed as obstacles the sprint skips ("Without more X, longer Y, or another Z"). No trailing period.',
          ),
        takeaway: z
          .string()
          .min(1)
          .describe(
            'The "What you walk away with" line — name the artifact the client keeps and what it gives them, as one page-ready sentence (cannibalization-safe: a tangible asset, not the paid program\'s outcome).',
          ),
        bottomCtaHeadline: z
          .string()
          .min(1)
          .describe(
            'Closing headline above the booking button — e.g. "Ready to see what\'s possible?". No trailing period.',
          ),
        objectionHandler: z
          .string()
          .min(1)
          .describe("Single-line objection reframe (price / time / fit)"),
      })
      .describe("Page copy rendered on the public Mini Sprint offer page"),
  })
  .refine(
    (sprint) =>
      sprint.sessions.every((session, index) => session.day === index + 1),
    {
      message:
        "sessions must be ordered Day 1 → Day 5 (each session's `day` matches its index + 1)",
      path: ["sessions"],
    },
  );

/**
 * What a NEW Mini Sprint save must satisfy: the read schema plus the Day-5
 * sales guide. Reads keep using `miniSprintDeliverableSchema`, so rows saved
 * before the guide existed still parse; only `create_deliverable` (new saves
 * and revisions alike) enforces these two fields.
 */
export const miniSprintWriteSchema = miniSprintDeliverableSchema.superRefine(
  (sprint, ctx) => {
    const required = {
      salesConversation:
        "the sales-conversation guide for the Day-5 close (opening, qualifying, objectionBranches, pricingOpener, closeOptions)",
      notNowFollowUp:
        "the follow-up for a client who says not now ({ message, timing })",
    } as const;
    for (const [field, what] of Object.entries(required)) {
      if (sprint.closeMechanics[field as keyof typeof required]) continue;
      ctx.addIssue({
        code: "custom",
        path: ["closeMechanics", field],
        message: `closeMechanics.${field} is required on a new Mini Sprint: add ${what}, confirm it with the coach, then save again`,
      });
    }
  },
);

/**
 * Video Scripts deliverable — 90-second authority video script + 3
 * short-form scripts + discovery call conversation map. Produced by the
 * `authority-video` skill (M4) from spec §3 Sub-Agent 4.
 *
 * Structural invariants encoded in the schema:
 *   - `authorityScript` requires all 5 SSS sections: opening, struggle,
 *     ship, shore, closingLine.
 *   - `shortFormScripts` MUST contain exactly 3 entries.
 *   - Each short-form script's archetype is one of the 5 canonical post
 *     archetypes from spec (`reframe`/`mirror`/`intelligence`/`origin`/
 *     `framework`).
 *   - Short-form duration falls in 30–60 seconds.
 *   - `discoveryCallMap` requires every field — opening, storyToQualifying,
 *     pricingOpener, at least 1 objectionBranch, at least 1 closeOption.
 *
 * Quality rules (read-aloud test, opening-line specificity, quotable
 * closing line) live in the skill's instructions.md — the schema only
 * rejects content that is structurally unfit for the deliverable shape.
 */

const VIDEO_SHORT_FORM_COUNT = 3;
const VIDEO_SHORT_FORM_MIN_SECONDS = 30;
const VIDEO_SHORT_FORM_MAX_SECONDS = 60;

export const videoShortFormArchetypeSchema = z.enum([
  "reframe",
  "mirror",
  "intelligence",
  "origin",
  "framework",
]);

export const videoShortFormScriptSchema = z.object({
  archetype: videoShortFormArchetypeSchema.describe(
    "One of the 5 canonical post archetypes from spec §3 — reframe, mirror, intelligence, origin, framework",
  ),
  body: z
    .string()
    .min(1)
    .describe(
      "Spoken-format script body for the short-form video (vertical, 30–60s)",
    ),
  hookFirst3Seconds: z
    .string()
    .min(1)
    .describe(
      "Opening hook line — the first 3 seconds. Must stop the scroll on its own.",
    ),
  estimatedDurationSeconds: z
    .number()
    .int()
    .min(
      VIDEO_SHORT_FORM_MIN_SECONDS,
      `Short-form duration must be at least ${VIDEO_SHORT_FORM_MIN_SECONDS} seconds`,
    )
    .max(
      VIDEO_SHORT_FORM_MAX_SECONDS,
      `Short-form duration must be at most ${VIDEO_SHORT_FORM_MAX_SECONDS} seconds`,
    )
    .describe("Estimated read-aloud duration in seconds (30–60)"),
});

export const videoScriptsDeliverableSchema = z.object({
  authorityScript: z
    .object({
      body: z
        .string()
        .min(1)
        .describe(
          "Full teleprompter-formatted 90s script body — short lines, pause markers, emphasis cues",
        ),
      structure: z
        .object({
          opening: z
            .string()
            .min(1)
            .describe(
              "Opening line — specific enough that the ICP thinks 'that's me' within 5 seconds",
            ),
          struggle: z
            .string()
            .min(1)
            .describe("The Struggle — the trigger problem in ICP language"),
          ship: z
            .string()
            .min(1)
            .describe(
              "The Ship — how the coach gets the client from struggle to shore",
            ),
          shore: z
            .string()
            .min(1)
            .describe("The Shore — the desired state, in specific nouns"),
          closingLine: z
            .string()
            .min(1)
            .describe(
              "Closing line — quotable, memorable, feels like a line from a speech (not a tagline)",
            ),
        })
        .describe(
          "SSS structure — every section is required so the script is structurally complete",
        ),
      visualGuidance: z
        .string()
        .min(1)
        .describe("B-roll, setting, and wardrobe notes for the recording"),
      estimatedDurationSeconds: z
        .number()
        .int()
        .positive()
        .describe(
          "Estimated read-aloud duration — typically ~90s for the authority script",
        ),
    })
    .describe("90-second authority video script — SSS structured"),
  shortFormScripts: z
    .array(videoShortFormScriptSchema)
    .length(
      VIDEO_SHORT_FORM_COUNT,
      `shortFormScripts must contain exactly ${VIDEO_SHORT_FORM_COUNT} entries`,
    )
    .describe(
      "Exactly 3 short-form scripts — independent pieces, each built for one specific hook (NOT shortened versions of the authority script)",
    ),
  discoveryCallMap: z
    .object({
      opening: z
        .string()
        .min(1)
        .describe("Opening of the discovery conversation"),
      storyToQualifying: z
        .string()
        .min(1)
        .describe(
          "Transition from the anchor story into the qualifying questions",
        ),
      objectionBranches: z
        .array(objectionBranchSchema)
        .min(
          1,
          "discoveryCallMap.objectionBranches must contain at least 1 branch",
        )
        .describe(
          "One branch per objection from icp_definition.objectionsLandscape (pre-call objections plus the master objection), each with a reframe",
        ),
      pricingOpener: z
        .string()
        .min(1)
        .describe(
          "Pricing opener — derived from core_offer.price: what the result is worth to the client, then the number",
        ),
      closeOptions: z
        .array(z.string().min(1))
        .min(1, "discoveryCallMap.closeOptions must contain at least 1 option")
        .describe("Close options the coach can offer at the end of the call"),
    })
    .describe(
      "Discovery call conversation map — branches for objections + pricing (NOT a teleprompter)",
    ),
});

/**
 * Mini Sprint Video Script deliverable — a 2:30 to 3:00 spoken video
 * script the coach records and drops into the Mini Sprint offer page.
 *
 * Structural invariants encoded in the schema:
 *   - exactly 5 sections, ordered Section 1 → 5
 *   - each section's `estimatedDurationSeconds` falls inside its allowed
 *     range (S1: 22–30, S2: 30–42, S3: 60–80, S4: 20–30, S5: 10–15)
 *   - section 3 carries a `dayByDay` of exactly 5 entries (Day 1 → 5)
 *   - `outline.sections` mirrors `sections` (5 entries, ordered)
 *   - each outline section has 3 to 7 beats
 *   - total estimated duration sits between 150 and 200 seconds
 *
 * Quality concerns (read-aloud feel, hedge ceiling, no next-step
 * language, voice fit, the literal CTA template wording) live in the
 * skill's instructions.md. The schema deliberately does not enforce
 * literal string content — that would duplicate guardrails the model
 * already follows from its instructions and adds two layers of
 * brittleness without buying real safety.
 */

const SPRINT_VIDEO_SCRIPT_SECTION_COUNT = 5;
const SPRINT_VIDEO_SCRIPT_DAY_COUNT = 5;
const SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MIN = 3;
const SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MAX = 7;
const SPRINT_VIDEO_SCRIPT_TOTAL_MIN_SECONDS = 150;
const SPRINT_VIDEO_SCRIPT_TOTAL_MAX_SECONDS = 200;

/** Per-section duration windows (inclusive). Ordered Section 1 → 5. */
const SPRINT_VIDEO_SCRIPT_SECTION_DURATIONS: ReadonlyArray<{
  min: number;
  max: number;
}> = [
  { min: 22, max: 30 },
  { min: 30, max: 42 },
  { min: 60, max: 80 },
  { min: 20, max: 30 },
  { min: 10, max: 15 },
];

export const miniSprintVideoScriptCredibilityPathSchema = z.enum([
  "personal_experience",
  "results_for_others",
  "formal_training",
  "hybrid",
]);

const miniSprintVideoScriptDayByDaySchema = z.object({
  day: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Day index, 1–5 (the dayByDay array must be ordered Day 1 → 5)"),
  whatWeDo: z
    .string()
    .min(1)
    .describe(
      "One sentence on what the session does that day, in spoken cadence",
    ),
  benefit: z
    .string()
    .min(1)
    .describe("One sentence on the benefit the viewer walks away with"),
});

const miniSprintVideoScriptSectionSchema = z.object({
  section: z
    .number()
    .int()
    .min(1)
    .max(5)
    .describe("Section index, 1–5 (must equal its array position + 1)"),
  name: z
    .string()
    .min(1)
    .describe("Display name (e.g. 'Hero recognition + pain')"),
  body: z.string().min(1).describe("Spoken section body"),
  estimatedDurationSeconds: z
    .number()
    .int()
    .positive()
    .describe(
      "Read-aloud duration in seconds. Per-section bounds enforced at the parent level.",
    ),
  dayByDay: z
    .array(miniSprintVideoScriptDayByDaySchema)
    .length(
      SPRINT_VIDEO_SCRIPT_DAY_COUNT,
      `dayByDay must contain exactly ${SPRINT_VIDEO_SCRIPT_DAY_COUNT} entries (Day 1 → 5)`,
    )
    .optional()
    .describe(
      "Per-day walkthrough — required on Section 3 only, omitted on the others",
    ),
});

const miniSprintVideoScriptOutlineSectionSchema = z.object({
  section: z.number().int().min(1).max(5),
  name: z.string().min(1),
  estimatedDurationSeconds: z.number().int().positive(),
  beats: z
    .array(z.string().min(1))
    .min(
      SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MIN,
      `outline beats must contain at least ${SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MIN} entries`,
    )
    .max(
      SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MAX,
      `outline beats must contain at most ${SPRINT_VIDEO_SCRIPT_OUTLINE_BEATS_MAX} entries`,
    )
    .describe(
      "3 to 7 short beats. Each beat is a content cue the coach paraphrases on camera.",
    ),
  anchorPhrases: z
    .array(z.string().min(1))
    .describe(
      "Verbatim phrases that must be said exactly (the truth-line, the offer-name line, the CTA literal). May be empty for sections without anchored lines.",
    ),
});

export const miniSprintVideoScriptDeliverableSchema = z
  .object({
    sprintName: z
      .string()
      .min(1)
      .describe("Sprint name lifted from the mini_sprint deliverable"),
    icpContextLine: z
      .string()
      .min(1)
      .describe(
        "Short ICP context line confirmed with the coach in the opener",
      ),
    credibilityPath: miniSprintVideoScriptCredibilityPathSchema.describe(
      "Which credibility path Section 2 draws from",
    ),
    credibilityPathNotes: z
      .string()
      .min(1)
      .describe("One-line note explaining the credibility-path choice"),
    testimonialSlot: z
      .object({
        included: z.boolean(),
        placement: z
          .string()
          .min(1)
          .describe(
            "Where the slot lives — 'end of section 2' when included, 'omitted' otherwise",
          ),
        notes: z.string().nullable(),
      })
      .describe("Optional testimonial slot at the end of Section 2"),
    sections: z
      .array(miniSprintVideoScriptSectionSchema)
      .length(
        SPRINT_VIDEO_SCRIPT_SECTION_COUNT,
        `sections must contain exactly ${SPRINT_VIDEO_SCRIPT_SECTION_COUNT} entries (Section 1 → 5)`,
      )
      .describe("Five script sections in fixed order"),
    fullScript: z
      .string()
      .min(1)
      .describe(
        "Concatenated section bodies with one blank line between sections",
      ),
    estimatedTotalDurationSeconds: z
      .number()
      .int()
      .min(
        SPRINT_VIDEO_SCRIPT_TOTAL_MIN_SECONDS,
        `total duration must be at least ${SPRINT_VIDEO_SCRIPT_TOTAL_MIN_SECONDS} seconds`,
      )
      .max(
        SPRINT_VIDEO_SCRIPT_TOTAL_MAX_SECONDS,
        `total duration must be at most ${SPRINT_VIDEO_SCRIPT_TOTAL_MAX_SECONDS} seconds`,
      )
      .describe("Total script duration at 165-wpm validation pace"),
    visualGuidance: z
      .string()
      .min(1)
      .describe("Framing, lighting, single-take suggestion"),
    outline: z
      .object({
        sections: z
          .array(miniSprintVideoScriptOutlineSectionSchema)
          .length(
            SPRINT_VIDEO_SCRIPT_SECTION_COUNT,
            `outline.sections must contain exactly ${SPRINT_VIDEO_SCRIPT_SECTION_COUNT} entries`,
          ),
      })
      .describe("Memorization outline the coach delivers from on camera"),
  })
  .superRefine((script, ctx) => {
    // Sections must be ordered Section 1 → 5.
    for (let i = 0; i < script.sections.length; i += 1) {
      const section = script.sections[i];
      if (section.section !== i + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", i, "section"],
          message: `sections must be ordered 1 → ${SPRINT_VIDEO_SCRIPT_SECTION_COUNT} (each section's \`section\` field equals its index + 1)`,
        });
      }

      const bounds = SPRINT_VIDEO_SCRIPT_SECTION_DURATIONS[i];
      if (
        bounds &&
        (section.estimatedDurationSeconds < bounds.min ||
          section.estimatedDurationSeconds > bounds.max)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", i, "estimatedDurationSeconds"],
          message: `Section ${i + 1} duration must be between ${bounds.min} and ${bounds.max} seconds (got ${section.estimatedDurationSeconds})`,
        });
      }
    }

    // dayByDay is required on Section 3 only and forbidden elsewhere.
    for (let i = 0; i < script.sections.length; i += 1) {
      const section = script.sections[i];
      const isPlanSection = i === 2;
      if (isPlanSection && !section.dayByDay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", i, "dayByDay"],
          message:
            "Section 3 (the plan) must include a `dayByDay` array of 5 entries",
        });
      }
      if (!isPlanSection && section.dayByDay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", i, "dayByDay"],
          message: `dayByDay is only valid on Section 3 (got it on Section ${i + 1})`,
        });
      }
      if (isPlanSection && section.dayByDay) {
        section.dayByDay.forEach((entry, dayIndex) => {
          if (entry.day !== dayIndex + 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["sections", i, "dayByDay", dayIndex, "day"],
              message: `dayByDay must be ordered Day 1 → 5 (entry at index ${dayIndex} reports day ${entry.day})`,
            });
          }
        });
      }
    }

    // Outline sections must mirror script sections.
    for (let i = 0; i < script.outline.sections.length; i += 1) {
      const outlineSection = script.outline.sections[i];
      if (outlineSection.section !== i + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["outline", "sections", i, "section"],
          message: `outline.sections must be ordered 1 → ${SPRINT_VIDEO_SCRIPT_SECTION_COUNT}`,
        });
      }
    }
  });

/**
 * LinkedIn Profile deliverable — a copy-pasteable pack of LinkedIn profile
 * sections. Produced by the `linkedin-profile` skill (Tier 1 of Sub-Agent 1).
 *
 * Length caps reflect LinkedIn's published platform limits as of 2026:
 *   - Headline: 220 chars
 *   - About: 2,600 chars
 *
 * The other sections are guidance prose for the coach (no LinkedIn-side
 * length cap), but we cap them at 4,000 chars to prevent runaway output.
 *
 * Tier 1 sections (`headline`, `about`, `featured`, `services`) are required
 * because they are the "do immediately" stack from spec §3 Sub-Agent 1.
 * `experience` is Tier 2 — optional, surfaced when the coach wants it.
 */
// Exported so the coach-edit field config mirrors these exact caps instead of
// restating them (a second copy could silently drift from the schema).
export const LINKEDIN_HEADLINE_MAX_CHARS = 220;
export const LINKEDIN_ABOUT_MAX_CHARS = 2600;
export const LINKEDIN_GUIDANCE_MAX_CHARS = 4000;

const linkedInProfileSectionKeySchema = z.enum([
  "headline",
  "about",
  "featured",
  "services",
  "experience",
]);

const linkedInProfileSectionTierSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

export const linkedInProfileSectionSchema = z
  .object({
    key: linkedInProfileSectionKeySchema.describe(
      "Which LinkedIn section this block targets (headline / about / featured / services / experience)",
    ),
    tier: linkedInProfileSectionTierSchema.describe(
      "Tier indicator from spec §3 Sub-Agent 1 — 1 = do today, 2 = this week, 3 = later",
    ),
    value: z
      .string()
      .min(1)
      .describe(
        "Pre-adapted content. Headline ≤ 220 chars. About ≤ 2600 chars. Other sections are guidance prose ≤ 4000 chars.",
      ),
    rationale: z
      .string()
      .min(1)
      .describe(
        "One-line justification grounding this section in the coach's strategy + voice deliverables",
      ),
  })
  .superRefine((section, ctx) => {
    const len = section.value.length;
    if (section.key === "headline" && len > LINKEDIN_HEADLINE_MAX_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `LinkedIn headline must be ≤ ${LINKEDIN_HEADLINE_MAX_CHARS} characters (got ${len})`,
      });
    }
    if (section.key === "about" && len > LINKEDIN_ABOUT_MAX_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `LinkedIn About must be ≤ ${LINKEDIN_ABOUT_MAX_CHARS} characters (got ${len})`,
      });
    }
    if (
      (section.key === "featured" ||
        section.key === "services" ||
        section.key === "experience") &&
      len > LINKEDIN_GUIDANCE_MAX_CHARS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `LinkedIn ${section.key} guidance must be ≤ ${LINKEDIN_GUIDANCE_MAX_CHARS} characters (got ${len})`,
      });
    }
  });

const LINKEDIN_PROFILE_REQUIRED_KEYS = [
  "headline",
  "about",
  "featured",
  "services",
] as const;

export const linkedInProfileDeliverableSchema = z
  .object({
    summary: z
      .string()
      .nullable()
      .describe("Optional one-line summary of the proposed update set"),
    sections: z
      .array(linkedInProfileSectionSchema)
      .min(LINKEDIN_PROFILE_REQUIRED_KEYS.length)
      .describe(
        "One entry per produced LinkedIn section. Tier 1 sections (headline, about, featured, services) are required.",
      ),
  })
  .superRefine((val, ctx) => {
    const keys = val.sections.map((s) => s.key);
    const seen = new Set(keys);
    if (seen.size !== keys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sections"],
        message: "sections must not contain duplicate keys",
      });
    }
    for (const required of LINKEDIN_PROFILE_REQUIRED_KEYS) {
      if (!seen.has(required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections"],
          message: `sections is missing required Tier 1 entry "${required}"`,
        });
      }
    }
  });

// ---------------------------------------------------------------------------
// Identity Statement (Plan step 2 — Identity Architect)
//
// The coach's BEING-level identity, output as three registers: the speakable
// POSITIONING version they say out loud, the function-rich PUBLIC version for
// marketing copy, and the stripped FELT version for daily practice, plus a
// one-week protocol that makes the felt version real.
// `operatingLevel` records the level of the coach's ORIGINAL self-description
// BEFORE the work (doing/having/being), not the resulting identity — it is
// the diagnostic input the step's five-phase conversation starts from.
// ---------------------------------------------------------------------------

/** The level a coach's self-description operates at, before the Identity Architect work. */
export const IDENTITY_OPERATING_LEVELS = ["doing", "having", "being"] as const;

/** Whether the coach's stated ambition matches the identity that emerged, or a mismatch was named honestly rather than papered over. */
export const IDENTITY_AMBITION_ALIGNMENT_VALUES = [
  "aligned",
  "mismatch_named",
] as const;

/**
 * The four story beats captured passively during the Identity Architect
 * conversation (v1.3 amendment Change 3). Harvested, never interviewed — if a
 * beat hasn't surfaced naturally by the close, the step marks it `thin` with at
 * most one gentle prompt, and Build's story skill collects it later. The
 * `discovery` beat is the seed of the coach's signature mechanism: when
 * Movement 5 names it, it links back to this beat. Zone 1 — the coach's story
 * never feeds the collective library.
 */
export const STORY_BEAT_KEYS = [
  "low_point",
  "search",
  "discovery",
  "why",
] as const;

/** A captured story beat. Every beat stores the coach's own words plus a thin-flag; a thin beat has no substantive text. */
export const storyBeatSchema = z.object({
  key: z.enum(STORY_BEAT_KEYS).describe("Which of the four beats this is"),
  text: z
    .string()
    .describe(
      "The beat in the coach's own words, as surfaced in conversation — verbatim where possible",
    ),
  thin: z
    .boolean()
    .describe(
      "True when the beat never surfaced naturally and only one gentle prompt was allowed — Build's story skill collects it later",
    ),
});

export const completeStoryBeatsSchema = z
  .array(storyBeatSchema)
  .length(STORY_BEAT_KEYS.length)
  .refine(
    (beats) => new Set(beats.map((beat) => beat.key)).size === beats.length,
    "Story beats must use each key exactly once",
  );

export const identityStatementDeliverableSchema = z.object({
  /**
   * The third register (Cohort 2): what the coach SAYS OUT LOUD. Optional at the
   * schema level so statements saved before this register existed still parse —
   * the step prompt requires it for every new save.
   */
  positioningVersion: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The speakable statement — 'I am an X coach for Y people'. Short, plain, no method, no mechanism, no cleverness",
    ),
  publicVersion: z
    .string()
    .min(1)
    .describe(
      "The function-rich, marketing-ready identity statement — the register other people read",
    ),
  feltVersion: z
    .string()
    .min(1)
    .describe(
      "The stripped, first-person version for daily practice — what the coach says to themselves",
    ),
  practiceProtocol: z
    .array(z.string())
    .describe(
      "Concrete one-week practice steps that make the felt version real",
    ),
  operatingLevel: z
    .enum(IDENTITY_OPERATING_LEVELS)
    .describe(
      "The level of the coach's ORIGINAL self-description BEFORE this work — doing (what I do), having (what I've built), or being (who I am)",
    ),
  candidatesConsidered: z
    .array(z.string())
    .max(3)
    .optional()
    .describe(
      "Up to 3 genuinely different BEING candidates explored during the body-test, kept for the record",
    ),
  /** The four story beats harvested from this conversation (v1.3 amendment Change 3). Zone 1. */
  storyBeats: completeStoryBeatsSchema.describe(
    "The four story beats (low point / search / discovery / why) in the coach's own words, each thin-flagged if it never surfaced naturally",
  ),
  reconciliation: z
    .object({
      ambitionAlignment: z
        .enum(IDENTITY_AMBITION_ALIGNMENT_VALUES)
        .describe(
          "Whether this identity's scale matches the coach's ambition_level, or a mismatch was named and left for the coach to resolve",
        ),
      note: z
        .string()
        .optional()
        .describe("Plain-language note on the mismatch, when named"),
    })
    .optional()
    .describe(
      "Present only when an ambition_level exists to reconcile against — absent when the coach has no ambition captured yet",
    ),
});

// ---------------------------------------------------------------------------
// Coach Story (Build — the "Your story" skill)
//
// Five reusable cuts of one origin story, each with its own job, reader, and
// length. The word bounds below are the ones the skill instructions state, and
// they are enforced here rather than trusted to the model: a cut outside its
// bound cannot do the job the cut exists for (a 300-word "short bio" is not a
// bio a host reads out).
// ---------------------------------------------------------------------------

/** Whitespace-delimited token count — the unit every `coach_story` word bound is measured in. */
function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** A non-empty string whose word count sits inside `[min, max]`. */
function wordBounded(min: number, max: number, field: string) {
  return z
    .string()
    .min(1)
    .refine((text) => countWords(text) >= min, {
      message: `${field} must be at least ${min} words`,
    })
    .refine((text) => countWords(text) <= max, {
      message: `${field} must be at most ${max} words`,
    });
}

/** A closing quote or bracket, which may legitimately sit after a terminator. */
const CLOSERS = /["'\u2019\u201d)\]]+$/;

/**
 * A terminator that actually closes a sentence: one followed by whitespace or
 * the end of the text, optionally through a closing quote or bracket.
 */
const SENTENCE_END = /[.!?](?=["'\u2019\u201d)\]]*(?:\s|$))/g;

/** A dotted abbreviation or initialism — "U.S.", "e.g.", "a.m.". */
const DOTTED_ABBREVIATION = /\b(?:[A-Za-z]\.){2,}/g;

/**
 * True when the text carries at most one sentence terminator, and that
 * terminator ends the text. A warm one-liner that runs to two sentences is a
 * paragraph, and reads as one in a DM.
 *
 * Counting every `.!?` rejects four one-liners that are genuinely one
 * sentence, so each gets a rule of its own — none of them is obvious from the
 * regexes alone:
 *
 *  1. Ellipsis — "I still do this for the person I was at 34..." Both "..."
 *     and "…" collapse to a single terminator before anything is counted.
 *  2. Abbreviation — "I coach ex-U.S. Army officers." The second dot of
 *     "U.S." is followed by a space, so the followed-by-non-space rule alone
 *     misses it; dotted initialisms are replaced wholesale instead.
 *  3. Decimal — "1.5 million people leave every year." That dot is followed
 *     by a digit, so it never reads as a sentence end.
 *  4. Terminator inside a closing quote — 'someone once told me "you're not
 *     the problem."' The quote after the full stop still ends the text.
 */
function isSingleSentence(text: string): boolean {
  const normalized = text
    .trim()
    .replace(/\.{3}|\u2026/g, ".")
    .replace(DOTTED_ABBREVIATION, "X");
  const terminators = [...normalized.matchAll(SENTENCE_END)];
  if (terminators.length === 0) return true;
  if (terminators.length > 1) return false;
  const endOfProse = normalized.replace(CLOSERS, "").length;
  return terminators[0]?.index === endOfProse - 1;
}

/**
 * True when the text ends on a question, allowing a trailing closing quote.
 * The warm one-liner says why the coach does this work; a question hands the
 * reader something to answer instead, which is what the field description
 * already promises it will not do.
 */
function endsInQuestion(text: string): boolean {
  return text.trim().replace(CLOSERS, "").endsWith("?");
}

export const coachStoryDeliverableSchema = z.object({
  beats: completeStoryBeatsSchema.describe(
    "All four story beats (low_point, search, discovery, why) as they now stand — the ones Plan captured plus any collected in this skill, each key used exactly once. A beat the coach declined stays thin: true",
  ),
  profileAbout: wordBounded(130, 170, "profileAbout").describe(
    "130 to 170 words, first person. The full arc — where they were, what they tried, what they found, why it matters, who it is for now. Ends on the reader, not on the coach. Used as the DCM profile About and the LinkedIn About",
  ),
  vslBeat: wordBounded(130, 170, "vslBeat").describe(
    "130 to 170 words, first person, written to be SPOKEN at roughly 60 seconds. Short sentences, one idea per breath, landing on the signature mechanism. Used inside video scripts",
  ),
  contentHook: wordBounded(1, 40, "contentHook").describe(
    "One or two lines, 40 words maximum. The sharpest turn in the story, written so it opens a post or an article cold with no setup",
  ),
  warmOneLiner: wordBounded(1, 30, "warmOneLiner")
    .refine(isSingleSentence, {
      message: "warmOneLiner must be a single sentence",
    })
    .refine((text) => !endsInQuestion(text), {
      message: "warmOneLiner must not end in a question",
    })
    .describe(
      "ONE sentence, 30 words maximum. Why this coach does this work, said the way they would say it to one person. Does not sell, does not name the offer, does not end in a question. Used in DMs and outreach openers",
    ),
  shortBio: wordBounded(40, 60, "shortBio").describe(
    "40 to 60 words, THIRD person — this is what a host reads out before the coach speaks. Name, who they work with, the mechanism, one line of why. No adjective stacking",
  ),
});

// ---------------------------------------------------------------------------
// Ambition Level (captured post-reveal in Diagnose, or fallback-captured in
// the Identity Architect step)
//
// A code-stamped deliverable — this is written
// directly via `upsertDeliverable` from a completed interactive flow, never
// through the model-authored `create_deliverable` path, so there is nothing
// here for the model to fabricate. `capturedIn` records which surface ran the
// capture, since the same three axes (scale, revenue target + horizon, life
// shape) can be gathered in either room.
// ---------------------------------------------------------------------------

/** How big the coach wants this business to become. */
export const AMBITION_SCALE_VALUES = [
  "lifestyle",
  "six_figure_solo",
  "seven_figure_leveraged",
  "bigger",
] as const;

/** How much of the work the coach wants to keep doing themselves vs. hand off. */
export const AMBITION_LEVERAGE_APPETITE_VALUES = [
  "solo",
  "small_team",
  "leveraged",
] as const;

/** Where the ambition axes were captured — Diagnose is the primary surface; Plan is the fallback for a coach who skipped or declined it. */
export const AMBITION_CAPTURED_IN_VALUES = ["diagnose", "plan"] as const;

export const ambitionLevelDeliverableSchema = z.object({
  scale: z
    .enum(AMBITION_SCALE_VALUES)
    .describe("How big the coach wants this business to become"),
  revenueTarget: z
    .object({
      amount: z
        .number()
        .int()
        .positive()
        .describe("Target annual revenue in the coach's chosen currency"),
      currency: z
        .string()
        .regex(/^[A-Za-z]{3}$/)
        .optional()
        .describe("Three-letter currency code; USD is the default"),
      normalizedUsdAmount: z
        .number()
        .finite()
        .positive()
        .optional()
        .describe(
          "Target annual revenue normalized to USD for rule comparisons",
        ),
      horizonYears: z
        .union([z.literal(2), z.literal(3)])
        .describe(
          "The timeframe the revenue target is set against — 2 or 3 years",
        ),
    })
    .superRefine((target, context) => {
      const currency = (target.currency ?? "USD").toUpperCase();
      if (currency !== "USD" && target.normalizedUsdAmount === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["normalizedUsdAmount"],
          message: "A non-USD target requires a normalized USD amount",
        });
      }
      if (currency === "USD" && target.normalizedUsdAmount !== undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["normalizedUsdAmount"],
          message: "USD targets must use amount as the normalized USD amount",
        });
      }
    }),
  hoursPerWeek: z
    .number()
    .int()
    .positive()
    .max(80)
    .describe("Hours per week the coach wants to work toward this ambition"),
  leverageAppetite: z
    .enum(AMBITION_LEVERAGE_APPETITE_VALUES)
    .describe(
      "How much of the work the coach wants to keep doing solo vs. hand off",
    ),
  capturedIn: z
    .enum(AMBITION_CAPTURED_IN_VALUES)
    .describe(
      "Which room ran the capture — Diagnose (primary) or Plan (fallback)",
    ),
});

/**
 * Coach-provided axes for the Diagnose North Star capture. The server adds
 * `capturedIn` when it writes the durable deliverable.
 */
export const ambitionLevelInputSchema = ambitionLevelDeliverableSchema.omit({
  capturedIn: true,
});
export type AmbitionLevelInput = z.infer<typeof ambitionLevelInputSchema>;
export type AmbitionCapturedIn = z.infer<
  typeof ambitionLevelDeliverableSchema
>["capturedIn"];

// ---------------------------------------------------------------------------
// Coach intake (Plan step "Getting to know you")
//
// One record per coach, saved progressively and revised in place by the
// `save_coach_intake` tool. Every answer is optional so a coach who leaves
// mid-intake resumes where she stopped; `thin` names the answers she could not
// give, to be asked again by the skill that needs them. The keys follow
// Thomas's NOVA Opening Questions plus the three stage questions; the answers
// to questions 6, 7, 9 and 10 are saved where they are used (the core offer
// step, the LinkedIn profile and outreach skills), not by this step.
// ---------------------------------------------------------------------------

/** The answers a coach intake holds, which `thin` can name. */
export const COACH_INTAKE_FIELDS = [
  "stage",
  "coachingTopic",
  "whoPays",
  "method",
  "background",
  "clientWords",
  "dreamClient",
  "currentOffer",
  "clientStories",
  "warmNetwork",
  "triedBefore",
  "links",
  "timeAvailable",
  "anythingElse",
] as const;
export type CoachIntakeField = (typeof COACH_INTAKE_FIELDS)[number];

/** A share as a whole number from 0 to 100, so 80 means 80%, never 0.8. */
const intakePercentSchema = z.number().min(0).max(100);

const coachIntakeStageSchema = z
  .object({
    hasPayingClients: z
      .boolean()
      .describe(
        "Question 0: whether anyone has paid her for coaching, even one person. Practice clients, free sessions and friends do not count",
      ),
    paidClientsLast12Months: z
      .number()
      .int()
      .min(0)
      .describe(
        "Roughly how many clients paid her in the last 12 months; 0 when nobody did",
      ),
    typicalPriceUsd: z
      .number()
      .min(0)
      .describe(
        "What one client typically pays her for the whole engagement, in USD. A coach who quotes another currency gives the USD amount herself; never invent an exchange rate",
      ),
    revenueSplit: z
      .object({
        directPercent: intakePercentSchema.describe(
          "Share of last year's coaching revenue from clients who paid her directly, as a whole number from 0 to 100 (80 means 80%)",
        ),
        corporatePercent: intakePercentSchema.describe(
          "Share from corporate contracts, as a whole number from 0 to 100 (20 means 20%)",
        ),
        consultingPercent: intakePercentSchema.describe(
          "Share from consulting, as a whole number from 0 to 100 (10 means 10%)",
        ),
      })
      .partial()
      .refine(
        (split) =>
          (split.directPercent ?? 0) +
            (split.corporatePercent ?? 0) +
            (split.consultingPercent ?? 0) <=
          100,
        "The shares of last year's revenue cannot add up to more than 100",
      ),
  })
  .partial();

const coachIntakeBackgroundSchema = z
  .object({
    yearsCoaching: z.number().min(0).max(80),
    clientsCoached: z
      .number()
      .int()
      .min(0)
      .describe("Roughly how many clients she has coached so far"),
    previousRole: z.string().min(1),
    previousIndustry: z.string().min(1),
    certification: z
      .string()
      .min(1)
      .describe('The certification and its level, or "none"'),
    livedTransformation: z
      .string()
      .min(1)
      .describe(
        "Whether she lived the problem she now coaches on, or has already helped people through it, in her words",
      ),
  })
  .partial();

const coachIntakeClientStorySchema = z
  .object({
    before: z.string().min(1),
    change: z.string().min(1),
    duration: z.string().min(1),
    permission: z.string().min(1),
  })
  .partial();

export const coachIntakeSchema = z.object({
  stage: coachIntakeStageSchema.optional(),
  coachingTopic: z
    .string()
    .min(1)
    .optional()
    .describe("Question 1: the kind of coaching, in her words"),
  whoPays: z
    .enum(["clients_themselves", "employer", "both"])
    .optional()
    .describe(
      "Whether her clients pay for themselves or a company hires her. Ask once when question 1 did not say",
    ),
  method: z
    .string()
    .min(1)
    .optional()
    .describe(
      "Question 2: how she takes a client from where they are to the outcome, and the method's name if it has one",
    ),
  background: coachIntakeBackgroundSchema
    .optional()
    .describe("Question 3: what gives a client a reason to trust her"),
  clientWords: z
    .array(z.string().min(1))
    .min(1)
    .max(8)
    .optional()
    .describe(
      "Question 4: what clients or prospects say is their main problem, quoted as close to their words as she remembers",
    ),
  dreamClient: z
    .object({
      who: z.string().min(1),
      outcome: z.string().min(1),
    })
    .partial()
    .optional()
    .describe(
      "Question 5: the person she would most love to work with and what she would help them achieve",
    ),
  currentOffer: z.string().min(1).optional(),
  clientStories: z.array(coachIntakeClientStorySchema).max(3).optional(),
  warmNetwork: z.string().min(1).optional(),
  triedBefore: z.string().min(1).optional(),
  links: z
    .object({
      linkedin: z.string().min(1),
      website: z.string().min(1),
      marketingMaterial: z.string().min(1),
      listSize: z.string().min(1),
      linkedinAbout: z
        .string()
        .min(1)
        .describe(
          "Her LinkedIn About section, pasted by her, verbatim. NOVA cannot read a profile from a link",
        ),
      linkedinExperience: z
        .string()
        .min(1)
        .describe("Her LinkedIn Experience section, pasted by her, verbatim"),
      writing: z
        .array(z.string().min(1))
        .max(5)
        .describe(
          "Posts, articles or other writing of her own that she shared, one entry each, verbatim. Send the whole list: it replaces what is saved",
        ),
    })
    .partial()
    .optional(),
  timeAvailable: z
    .object({
      fullTime: z
        .boolean()
        .describe("True when coaching is her full-time work"),
      hoursPerWeek: z.number().min(0).max(120),
      days: z.string().min(1),
    })
    .partial()
    .optional()
    .describe("Question 11: the time she can spend building each week"),
  anythingElse: z
    .string()
    .min(1)
    .optional()
    .describe("What she added when asked what NOVA did not ask"),
  helpSentence: z
    .string()
    .min(1)
    .optional()
    .describe(
      'The first "I help…" sentence NOVA built from her own words (the mirror)',
    ),
  thin: z
    .array(z.enum(COACH_INTAKE_FIELDS))
    .optional()
    .describe(
      "Answers she could not give after one or two tries, to be asked again by the skill that needs them",
    ),
  /** SERVER-OWNED: stamped when she confirms the close; never accepted from the model. */
  confirmedAt: z.string().datetime().optional(),
});
export type CoachIntake = z.infer<typeof coachIntakeSchema>;

/**
 * What `save_coach_intake` accepts: any subset of the answers, merged into the
 * saved record. `confirmed` marks that she confirmed the close; the server
 * stamps `confirmedAt` from it.
 */
export const coachIntakeInputSchema = coachIntakeSchema
  .omit({ confirmedAt: true })
  .extend({
    confirmed: z
      .boolean()
      .optional()
      .describe(
        'True only on the save after she confirms or corrects "Here is what I have about you", together with the "I help…" sentence',
      ),
  });
export type CoachIntakeInput = z.infer<typeof coachIntakeInputSchema>;

// ---------------------------------------------------------------------------
// Mindset profile (the Mindset check, before the coach's first outward action)
//
// One record per coach, written by code from the `save_mindset_profile` tool.
// `completed` holds her seven answers, the pattern NOVA read from them, her
// commitment and the first step she took. `declined` is her first "not now";
// `skipped` is final and never blocks again. `offered` only records that Plan
// made its one offer and gates nothing. The keys follow Thomas's NOVA
// Opening Questions, section "The Mindset Module".
// ---------------------------------------------------------------------------

/** The four patterns NOVA names after M1 to M7 (`ms_pattern`). */
export const MINDSET_PATTERNS = [
  "perfectionism",
  "fear_of_being_seen",
  "feedback_as_verdict",
  "not_a_marketer",
] as const;
export type MindsetPattern = (typeof MINDSET_PATTERNS)[number];

const mindsetAnswerSchema = z.string().min(1);

/** Her seven answers, in her words. */
export const mindsetAnswersSchema = z.object({
  m1: mindsetAnswerSchema.describe(
    "M1: whether she shares what she makes quickly or keeps working on it until it feels perfect",
  ),
  m2: mindsetAnswerSchema.describe(
    "M2: something she has wanted to do for her business but has not done yet, and what stops her",
  ),
  m3: mindsetAnswerSchema.describe(
    "M3: a moment someone said no or criticized her work, how she felt and what she did next",
  ),
  m4: mindsetAnswerSchema.describe(
    "M4: her first thought when an old colleague or friend sees her post about her coaching",
  ),
  m5: mindsetAnswerSchema.describe(
    "M5: which of the four lines (coach not a marketer, rather be discovered, just want to coach, coach and business builder) sounds most like her",
  ),
  m6: mindsetAnswerSchema.describe(
    "M6: how she described what she does the last time someone asked, and whether she said she is a coach",
  ),
  m7: mindsetAnswerSchema.describe(
    "M7: what she thinks the reason would be if nothing has changed in six months",
  ),
});

/** The one thing she put out during the session. */
export const mindsetFirstStepSchema = z.object({
  kind: z
    .enum(["warm_network_message", "headline"])
    .describe(
      "A message to one person from her warm network, or her first-version LinkedIn headline",
    ),
  text: z
    .string()
    .min(1)
    .describe("The final text that went out, after her changes"),
  recipient: z
    .string()
    .min(1)
    .optional()
    .describe("For a message: who it went to, first name and role is enough"),
  done: z
    .boolean()
    .describe(
      "True only when she said she sent or published it during the session",
    ),
});

/** What she put out after the commitment, one entry per thing. */
export const mindsetPutOutEntrySchema = z.object({
  at: z.string().datetime(),
  what: z.string().min(1),
});
export type MindsetPutOutEntry = z.infer<typeof mindsetPutOutEntrySchema>;

/** The parts of a finished check the model writes. */
export const mindsetCheckResultSchema = z.object({
  answers: mindsetAnswersSchema,
  patterns: z
    .array(z.enum(MINDSET_PATTERNS))
    .min(1)
    .max(2)
    .describe("The one or two patterns NOVA named from M1 to M7"),
  patternInHerWords: z
    .string()
    .min(1)
    .describe(
      "The pattern as NOVA named it to her, quoting her own words, in one or two sentences",
    ),
  sellingAttitude: z
    .string()
    .min(1)
    .describe("Her attitude to saying what she does and selling, from M6"),
  ownObstacle: z
    .string()
    .min(1)
    .describe("The obstacle she named herself, from M7"),
  commitment: z
    .string()
    .min(1)
    .describe(
      "Her one line on which coach she chooses to be from today, in her words",
    ),
  lettingGo: z
    .string()
    .min(1)
    .describe("The one old habit she is letting go of, in her words"),
  firstStep: mindsetFirstStepSchema,
});
export type MindsetCheckResult = z.infer<typeof mindsetCheckResultSchema>;

/**
 * The stored record. The server stamps the status, the dates, the decline
 * count and the put-out entries; the rest comes from a finished check.
 */
export const mindsetProfileSchema = z
  .object({
    status: z.enum(["offered", "completed", "declined", "skipped"]),
    declineCount: z.number().int().min(0),
    /** When Plan offered the check, and in which conversation. Once per coach. */
    offeredAt: z.string().datetime().optional(),
    offeredInConversationId: z.string().min(1).optional(),
    declinedAt: z.string().datetime().optional(),
    /** The conversation of the latest decline, so a repeated save is ignored. */
    declinedInConversationId: z.string().min(1).optional(),
    committedAt: z.string().datetime().optional(),
    putOut: z.array(mindsetPutOutEntrySchema).optional(),
  })
  .merge(mindsetCheckResultSchema.partial());
export type MindsetProfile = z.infer<typeof mindsetProfileSchema>;
export type MindsetStatus = MindsetProfile["status"];

/**
 * What `save_mindset_profile` accepts. A root object, never a union: the model
 * provider refuses a tool whose root schema is not an object.
 */
export const mindsetProfileInputSchema = z
  .object({
    outcome: z
      .enum(["completed", "declined"])
      .describe(
        '"completed" after the first step went out, with `result`. "declined" when she says she does not want the Mindset check now',
      ),
    result: mindsetCheckResultSchema
      .optional()
      .describe('Required with "completed"; leave it out with "declined"'),
    warmNetwork: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Onboarding question 8, when you asked it in this check: the people and groups from her existing network she named, in her words. Saved to her coach intake",
      ),
  })
  .superRefine((input, ctx) => {
    if (input.outcome === "completed" && input.result === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["result"],
        message: 'A "completed" save needs the result of the check.',
      });
    }
  });
export type MindsetProfileInput = z.infer<typeof mindsetProfileInputSchema>;

// ---------------------------------------------------------------------------
// 90-Day Plan (Grow room — U6)
//
// Generated from the coach's existing foundation (offer, ambition, latest
// diagnosis) into a concrete 90-day sequence. `basedOn` is SERVER-OWNED,
// mirroring `core_offer`'s `marketCheck`/`valueLeverScores` convention:
// stripped from any model payload at the create_deliverable choke point
// (U6) and stamped by server code from the coach's actual foundation rows,
// so a regenerate can diff against exactly what this plan was built on.
// ---------------------------------------------------------------------------

export const ninetyDayPlanMilestoneSchema = z.object({
  label: z.string().min(1).describe("Short name for this milestone"),
  targetDate: z
    .string()
    .describe("ISO date (YYYY-MM-DD) this milestone targets"),
  actions: z
    .array(z.string().min(1))
    .min(1)
    .describe(
      "Concrete prescriptive actions — reach-outs, LinkedIn activity, community check-ins, sprint runs — written in the coach's own numbers",
    ),
  measure: z
    .string()
    .min(1)
    .describe("How the coach knows this milestone was hit"),
});

/**
 * SERVER-OWNED provenance receipt: which foundation rows this plan was
 * generated from. Stripped from model payloads and stamped by server code —
 * see the section note above. Optional in the schema so an early draft (or a
 * legacy pre-provenance row) still validates.
 */
export const ninetyDayPlanBasedOnSchema = z.object({
  offerDeliverableId: z
    .string()
    .uuid()
    .describe("The core_offer row this plan was built on"),
  offerVersion: z
    .number()
    .int()
    .describe("The offer row's version at generation time"),
  ambitionVersion: z
    .number()
    .int()
    .optional()
    .describe(
      "The ambition_level row's version at generation time, when one exists",
    ),
  diagnosisSnapshotId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "The 6-Figure Readiness Score attempt this plan's focus reads from; plans made before it hold a retired score's snapshot id",
    ),
});

export const ninetyDayPlanDeliverableSchema = z.object({
  horizon: z.object({
    startDate: z.string().describe("ISO date (YYYY-MM-DD) the 90 days start"),
    endDate: z.string().describe("ISO date (YYYY-MM-DD) the 90 days end"),
  }),
  milestones: z
    .array(ninetyDayPlanMilestoneSchema)
    .min(1)
    .describe(
      "3 phases × ~4 fortnight/weekly milestones, most-immediate first",
    ),
  weeklyRhythm: z
    .array(z.string())
    .describe(
      "The recurring weekly cadence of activity underneath the milestones",
    ),
  // Server-owned field below — stripped from model payloads at the
  // create_deliverable choke point (U6), populated by server code only.
  basedOn: ninetyDayPlanBasedOnSchema.optional(),
});
